'use client';

import { c as _c } from "react/compiler-runtime";
import { ArrowDownOutlined, ArrowUpOutlined, DeleteOutlined } from '@ant-design/icons';
import { useSuspenseQuery } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { useRouter, useSearchParams } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDebounceValue } from 'usehooks-ts';
import { celebritiesListOptions } from '@/entities/Celebrity/api/celebritiesList';
import { experienceTypesOptions } from '@/entities/Experiences/api/experienceTypes';
import { getQueryClient } from '@/libs/queryClient';
import { FilterTags } from '@/shared/components/FilterTags';
import { SortRadioButton } from '@/shared/components/Sort/SortRadioButton';
import { SortRadioGroup } from '@/shared/components/Sort/SortRadioGroup';
import { ArrowDown } from '@/shared/icons/ArrowDown';
import { ArrowUp } from '@/shared/icons/ArrowUp';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import { CelebritySearchSelect } from '../CelebritySearchSelect';
import { AppliedFilters } from '../FilterActions';
import type { AppliedFilter } from '../FilterActions/AppliedFilters';
import { PriceSelect } from '../PriceSelect/PriceSelect';
interface FilterExperiencesProps {
  onFilterChange?: (filters: FilterValues) => void;
  initialFilters?: Partial<FilterValues>;
  className?: string;
  /**
   * Цвет фона для всех селектов
   * @default #392f99
   */
  selectBackgroundColor?: string;
}
export interface FilterValues {
  celebrities?: number[];
  price?: [number, number];
  sortBy?: 'ee_current_price' | undefined;
  sortType?: 'asc' | 'desc' | undefined;
  type?: 'EXCLUSIVE_EXPERIENCE' | 'DIGITAL_EXPERIENCE' | 'EXCLUSIVE_ITEM' | 'all';
  page?: number;
  celebrityName?: string;
}
export const FilterExperiences: FC<FilterExperiencesProps> = t0 => {
  const $ = _c(111);
  const {
    className: t1
  } = t0;
  const className = t1 === undefined ? "" : t1;
  const t = useTranslations("experiences.filters");
  const router = useRouter();
  const locale = useLocale();
  const isMobile = useMediaQuery("(max-width: 1024px)");
  const [isOpen, setIsOpen] = useState(false);
  let t2;
  let t3;
  if ($[0] !== isMobile || $[1] !== isOpen) {
    t2 = () => {
      if (isMobile && isOpen) {
        const scrollY = window.scrollY;
        document.body.style.position = "fixed";
        document.body.style.top = `-${scrollY}px`;
        document.body.style.width = "100%";
        document.body.style.overflow = "hidden";
      } else {
        if (isMobile) {
          const scrollY_0 = document.body.style.top;
          document.body.style.position = "";
          document.body.style.top = "";
          document.body.style.width = "";
          document.body.style.overflow = "";
          if (scrollY_0) {
            window.scrollTo(0, parseInt(scrollY_0 || "0", 10) * -1);
          }
        }
      }
    };
    t3 = [isMobile, isOpen];
    $[0] = isMobile;
    $[1] = isOpen;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  const searchParams = useSearchParams();
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = experienceTypesOptions();
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const {
    data: t5
  } = useSuspenseQuery(t4);
  t5 === undefined ? [] : t5;
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = ["all", "EXCLUSIVE_EXPERIENCE", "DIGITAL_EXPERIENCE", "EXCLUSIVE_ITEM"];
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const tagsTypes = t6;
  let t7;
  if ($[6] !== t) {
    t7 = t("all");
    $[6] = t;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== t) {
    t8 = t("filter.exclusiveExperience");
    $[8] = t;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== t) {
    t9 = t("filter.digitalExperience");
    $[10] = t;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== t) {
    t10 = t("filter.exclusiveItem");
    $[12] = t;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== t10 || $[15] !== t7 || $[16] !== t8 || $[17] !== t9) {
    t11 = [t7, t8, t9, t10];
    $[14] = t10;
    $[15] = t7;
    $[16] = t8;
    $[17] = t9;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const tags = t11;
  let t12;
  if ($[19] !== searchParams) {
    t12 = searchParams.get("type");
    $[19] = searchParams;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  const t13 = t12 as 'EXCLUSIVE_EXPERIENCE' | 'DIGITAL_EXPERIENCE' | 'EXCLUSIVE_ITEM' | undefined;
  let t14;
  if ($[21] !== searchParams) {
    t14 = searchParams.get("celebrities") ? searchParams.get("celebrities")?.split(",").map(Number) : undefined;
    $[21] = searchParams;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== searchParams) {
    t15 = searchParams.get("price") ? searchParams.get("price")?.split(",").map(Number) as [number, number] : undefined;
    $[23] = searchParams;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== searchParams) {
    t16 = searchParams.get("celebrityName") ?? undefined;
    $[25] = searchParams;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] !== searchParams) {
    t17 = searchParams.get("sortBy");
    $[27] = searchParams;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  const t18 = t17 as 'ee_current_price' | undefined;
  let t19;
  if ($[29] !== searchParams) {
    t19 = searchParams.get("sortType");
    $[29] = searchParams;
    $[30] = t19;
  } else {
    t19 = $[30];
  }
  const t20 = t19 as 'asc' | 'desc' | undefined;
  let t21;
  if ($[31] !== searchParams) {
    t21 = searchParams.get("page") ? Number(searchParams.get("page")) : undefined;
    $[31] = searchParams;
    $[32] = t21;
  } else {
    t21 = $[32];
  }
  let t22;
  if ($[33] !== t13 || $[34] !== t14 || $[35] !== t15 || $[36] !== t16 || $[37] !== t18 || $[38] !== t20 || $[39] !== t21) {
    t22 = {
      type: t13,
      celebrities: t14,
      price: t15,
      celebrityName: t16,
      sortBy: t18,
      sortType: t20,
      page: t21
    };
    $[33] = t13;
    $[34] = t14;
    $[35] = t15;
    $[36] = t16;
    $[37] = t18;
    $[38] = t20;
    $[39] = t21;
    $[40] = t22;
  } else {
    t22 = $[40];
  }
  const initialFilters = t22;
  const t23 = initialFilters?.type ?? undefined;
  const t24 = initialFilters?.celebrities ?? undefined;
  const t25 = initialFilters?.price ?? undefined;
  const t26 = initialFilters?.celebrityName ?? undefined;
  const t27 = initialFilters?.sortBy ?? undefined;
  const t28 = initialFilters?.sortType ?? undefined;
  const t29 = initialFilters?.page ?? undefined;
  let t30;
  if ($[41] !== t23 || $[42] !== t24 || $[43] !== t25 || $[44] !== t26 || $[45] !== t27 || $[46] !== t28 || $[47] !== t29) {
    t30 = {
      type: t23,
      celebrities: t24,
      price: t25,
      celebrityName: t26,
      sortBy: t27,
      sortType: t28,
      page: t29
    };
    $[41] = t23;
    $[42] = t24;
    $[43] = t25;
    $[44] = t26;
    $[45] = t27;
    $[46] = t28;
    $[47] = t29;
    $[48] = t30;
  } else {
    t30 = $[48];
  }
  const [filters, setFilters] = useState(t30);
  const t31 = initialFilters?.type ?? undefined;
  const t32 = initialFilters?.celebrities ?? undefined;
  const t33 = initialFilters?.price ?? undefined;
  const t34 = initialFilters?.celebrityName ?? undefined;
  const t35 = initialFilters?.sortBy ?? undefined;
  const t36 = initialFilters?.sortType ?? undefined;
  const t37 = initialFilters?.page ?? undefined;
  let t38;
  if ($[49] !== t31 || $[50] !== t32 || $[51] !== t33 || $[52] !== t34 || $[53] !== t35 || $[54] !== t36 || $[55] !== t37) {
    t38 = {
      type: t31,
      celebrities: t32,
      price: t33,
      celebrityName: t34,
      sortBy: t35,
      sortType: t36,
      page: t37
    };
    $[49] = t31;
    $[50] = t32;
    $[51] = t33;
    $[52] = t34;
    $[53] = t35;
    $[54] = t36;
    $[55] = t37;
    $[56] = t38;
  } else {
    t38 = $[56];
  }
  const [localFilters, setLocalFilters] = useState(t38);
  let t39;
  if ($[57] !== initialFilters.celebrities || $[58] !== initialFilters.page || $[59] !== initialFilters.price || $[60] !== initialFilters.sortBy || $[61] !== initialFilters.sortType || $[62] !== initialFilters.type) {
    t39 = () => {
      setFilters(prev => ({
        ...prev,
        type: initialFilters?.type ?? undefined,
        celebrities: initialFilters?.celebrities ?? undefined,
        price: initialFilters?.price ?? undefined,
        sortBy: initialFilters?.sortBy ?? undefined,
        sortType: initialFilters?.sortType ?? undefined,
        page: initialFilters?.page ?? undefined
      }));
      setLocalFilters(prev_0 => ({
        ...prev_0,
        type: initialFilters?.type ?? undefined,
        celebrities: initialFilters?.celebrities ?? undefined,
        price: initialFilters?.price ?? undefined,
        sortBy: initialFilters?.sortBy ?? undefined,
        sortType: initialFilters?.sortType ?? undefined,
        page: initialFilters?.page ?? undefined
      }));
    };
    $[57] = initialFilters.celebrities;
    $[58] = initialFilters.page;
    $[59] = initialFilters.price;
    $[60] = initialFilters.sortBy;
    $[61] = initialFilters.sortType;
    $[62] = initialFilters.type;
    $[63] = t39;
  } else {
    t39 = $[63];
  }
  let t40;
  if ($[64] !== initialFilters) {
    t40 = [initialFilters];
    $[64] = initialFilters;
    $[65] = t40;
  } else {
    t40 = $[65];
  }
  useEffect(t39, t40);
  const [debouncedFilters] = useDebounceValue(filters, 500);
  let t41;
  if ($[66] !== debouncedFilters || $[67] !== locale || $[68] !== router || $[69] !== searchParams) {
    t41 = () => {
      const params = new URLSearchParams();
      Object.entries(debouncedFilters).forEach(t42 => {
        const [key, value] = t42;
        if (value) {
          params.set(key, value);
        } else {
          params.delete(key);
        }
      });
      if (params.toString() === searchParams.toString()) {
        return;
      }
      router.push(`/${locale}/auctions?${params.toString()}`);
    };
    $[66] = debouncedFilters;
    $[67] = locale;
    $[68] = router;
    $[69] = searchParams;
    $[70] = t41;
  } else {
    t41 = $[70];
  }
  let t42;
  if ($[71] !== debouncedFilters || $[72] !== locale || $[73] !== router.push) {
    t42 = [debouncedFilters, router.push, locale];
    $[71] = debouncedFilters;
    $[72] = locale;
    $[73] = router.push;
    $[74] = t42;
  } else {
    t42 = $[74];
  }
  useEffect(t41, t42);
  let t43;
  if ($[75] === Symbol.for("react.memo_cache_sentinel")) {
    t43 = (key_0, value_0) => {
      setFilters(prev_1 => ({
        ...prev_1,
        [key_0]: value_0
      }));
    };
    $[75] = t43;
  } else {
    t43 = $[75];
  }
  const handleFilterChange = t43;
  let t44;
  if ($[76] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = (key_1, value_1) => {
      setLocalFilters(prev_2 => ({
        ...prev_2,
        [key_1]: value_1
      }));
    };
    $[76] = t44;
  } else {
    t44 = $[76];
  }
  const handleLocalFilterChange = t44;
  let t45;
  if ($[77] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = celebritiesListOptions({});
    $[77] = t45;
  } else {
    t45 = $[77];
  }
  const {
    data: t46
  } = useSuspenseQuery(t45);
  let t47;
  if ($[78] !== t46) {
    t47 = t46 === undefined ? [] : t46;
    $[78] = t46;
    $[79] = t47;
  } else {
    t47 = $[79];
  }
  const celebrities = t47;
  getQueryClient();
  let t48;
  if ($[80] !== celebrities) {
    t48 = id => {
      const celebrity_0 = celebrities?.find(celebrity => celebrity.id === id);
      return `${celebrity_0?.first_name} ${celebrity_0?.last_name}`;
    };
    $[80] = celebrities;
    $[81] = t48;
  } else {
    t48 = $[81];
  }
  const getCelebrityShortNameById = t48;
  const getExperienceTypeNameById = _temp;
  let t49;
  if ($[82] !== filters || $[83] !== getCelebrityShortNameById) {
    t49 = Object.entries(filters).flatMap(t50 => {
      const [key_2, value_2] = t50;
      if (value_2) {
        let type;
        let mapper;
        bb329: switch (key_2) {
          case "celebrities":
            {
              mapper = getCelebrityShortNameById;
              break bb329;
            }
          case "types":
            {
              type = "type";
              mapper = getExperienceTypeNameById;
              break bb329;
            }
          case "tags":
            {
              type = "tag";
              break bb329;
            }
          default:
            {
              return;
            }
        }
        return Array.isArray(value_2) ? value_2.map(item => ({
          type,
          key: key_2,
          value: item,
          label: mapper ? mapper(item) : item
        })) : {
          type,
          key: key_2,
          value: value_2,
          label: mapper ? mapper(value_2) : value_2
        };
      }
    }).filter(Boolean);
    $[82] = filters;
    $[83] = getCelebrityShortNameById;
    $[84] = t49;
  } else {
    t49 = $[84];
  }
  const appliedFilters = t49 as AppliedFilter[];
  let t50;
  if ($[85] !== getCelebrityShortNameById || $[86] !== localFilters) {
    t50 = Object.entries(localFilters).flatMap(t51 => {
      const [key_3, value_3] = t51;
      if (value_3) {
        let type_0;
        let mapper_0;
        bb358: switch (key_3) {
          case "celebrities":
            {
              mapper_0 = getCelebrityShortNameById;
              break bb358;
            }
          case "types":
            {
              type_0 = "type";
              mapper_0 = getExperienceTypeNameById;
              break bb358;
            }
          case "tags":
            {
              type_0 = "tag";
              break bb358;
            }
          default:
            {
              return;
            }
        }
        return Array.isArray(value_3) ? value_3.map(item_0 => ({
          type: type_0,
          key: key_3,
          value: item_0,
          label: mapper_0 ? mapper_0(item_0) : item_0
        })) : {
          type: type_0,
          key: key_3,
          value: value_3,
          label: mapper_0 ? mapper_0(value_3) : value_3
        };
      }
    }).filter(Boolean);
    $[85] = getCelebrityShortNameById;
    $[86] = localFilters;
    $[87] = t50;
  } else {
    t50 = $[87];
  }
  const localAppliedFilters = t50 as AppliedFilter[];
  let t51;
  if ($[88] !== appliedFilters || $[89] !== className || $[90] !== filters || $[91] !== initialFilters.type || $[92] !== t || $[93] !== tags) {
    t51 = () => <><FilterTags tags={tags} selectedTag={initialFilters.type && tags[tagsTypes.indexOf(initialFilters.type)]} onTagSelect={tag => handleFilterChange("type", tagsTypes[tags.indexOf(tag)])} /><div className={`mt-7 flex w-full flex-col gap-4 ${className}`}><div className="flex gap-4"><CelebritySearchSelect value={filters.celebrities} onChange={value_4 => handleFilterChange("celebrities", value_4)} placeholder={t("filter.celebrities")} className="flex-1" searchValue={filters.celebrityName ?? ""} setSearchValue={value_5 => handleFilterChange("celebrityName", value_5)} /><PriceSelect popupClassName="[&>.ant-popover-content>.ant-popover-inner]:pr-8" className="flex-1" value={filters.price} onChange={value_6 => handleFilterChange("price", value_6)} placeholder={t("filter.price")} currency="USDC" min={0} max={16000} step={100} defaultValue={[0, 8000]} /><button type="button" className="ml-2 flex items-center gap-2 pl-3 text-base text-[#A0A3E2]" onClick={() => {
            handleFilterChange("celebrities", []);
            handleFilterChange("type", undefined);
            handleFilterChange("sortBy", undefined);
            handleFilterChange("sortType", undefined);
            handleFilterChange("price", undefined);
          }}><DeleteOutlined className="size-4" /><span className="text-base">{t("filter.removeAllFilters")}</span></button></div><div className="mb-9 flex gap-16 items-center"><SortRadioGroup className="shrink-0" sortBy={filters.sortBy} sortType={filters.sortType} onSortChange={t52 => {
            const {
              sortBy,
              sortType
            } = t52;
            handleFilterChange("sortType", sortType);
            handleFilterChange("sortBy", sortBy);
          }} showAll={true}><SortRadioButton sortBy="ee_current_price">{t("price")}</SortRadioButton><SortRadioButton sortBy="created_at">{t("createdAt")}</SortRadioButton></SortRadioGroup><AppliedFilters className="shrink-1" filters={appliedFilters} onRemove={t53 => {
            const {
              key: key_4,
              value: value_7
            } = t53;
            return handleFilterChange(key_4 as keyof FilterValues, filters[key_4 as keyof Pick<FilterValues, 'celebrities'>]?.filter(item_1 => item_1 !== value_7));
          }} /></div></div></>;
    $[88] = appliedFilters;
    $[89] = className;
    $[90] = filters;
    $[91] = initialFilters.type;
    $[92] = t;
    $[93] = tags;
    $[94] = t51;
  } else {
    t51 = $[94];
  }
  const renderDesktopFilter = t51;
  let t52;
  if ($[95] !== filters.celebrities || $[96] !== filters.celebrityName || $[97] !== filters.price || $[98] !== filters.sortBy || $[99] !== filters.sortType || $[100] !== initialFilters.type || $[101] !== isOpen || $[102] !== localAppliedFilters || $[103] !== localFilters || $[104] !== t || $[105] !== tags) {
    t52 = () => <><FilterTags tags={tags} selectedTag={initialFilters.type && tags[tagsTypes.indexOf(initialFilters.type)]} onTagSelect={tag_0 => handleFilterChange("type", tagsTypes[tags.indexOf(tag_0)])} /><div className="mb-4 mt-2 flex flex-row items-center justify-between gap-4"><button type="button" onClick={() => setIsOpen(!isOpen)} className="flex h-10 items-center gap-2 text-sm text-[#A0A3E2]"><img src="/icons/filter.svg" alt="Filter" className="size-4" />{t("title")}</button><SortRadioGroup className="shrink-0" sortBy={filters.sortBy} sortType={filters.sortType} onSortChange={t53 => {
          const {
            sortBy: sortBy_0,
            sortType: sortType_0
          } = t53;
          handleFilterChange("sortType", sortType_0);
          handleFilterChange("sortBy", sortBy_0);
        }} showAll={true}><SortRadioButton sortBy="ee_current_price">{t("price")}</SortRadioButton><SortRadioButton sortBy="created_at">{t("createdAt")}</SortRadioButton></SortRadioGroup></div><AnimatePresence>{isOpen && <motion.div initial={{
          opacity: 0,
          y: "100%"
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: "100%"
        }} transition={{
          duration: 0.3
        }} className="fixed bottom-0 left-0 right-0 top-[32px] z-[9997] flex flex-col gap-2 bg-[#100D32] p-4"><div className="flex h-full flex-col justify-between"><div className="flex flex-col grow shrink overflow-hidden"><div className="mb-6 flex items-center justify-between"><button type="button" className="flex h-10 items-center gap-3 px-2 font-['Montserrat'] text-xl font-semibold text-white"><img src="/icons/filter.svg" alt="Filter" className="size-6" />{t("title")}</button><button type="button" className="flex items-center gap-2 pl-3 pr-2 text-base text-[#A0A3E2]" onClick={() => {
                  handleLocalFilterChange("celebrities", []);
                  handleLocalFilterChange("price", undefined);
                }}><DeleteOutlined className="size-4" /><span className="text-base">{t("filter.removeAllFilters")}</span></button></div><div className="mb-4 flex flex-col gap-3"><PriceSelect open={true} popupClassName="[&>.ant-popover-content>.ant-popover-inner]:pr-8" className="mb-[148px]" value={localFilters.price} onChange={value_8 => handleLocalFilterChange("price", value_8)} placeholder={t("filter.price")} currency="USDC" min={0} max={16000} step={100} defaultValue={[0, 8000]} autoAdjustOverflow={false} /><CelebritySearchSelect value={localFilters.celebrities} onChange={value_9 => handleLocalFilterChange("celebrities", value_9)} placeholder={t("filter.celebrities")} className="w-full" searchValue={localFilters.celebrityName ?? ""} setSearchValue={value_10 => handleLocalFilterChange("celebrityName", value_10)} /></div><AppliedFilters filters={localAppliedFilters} onRemove={filter => handleLocalFilterChange(filter.key as keyof FilterValues, localFilters[filter.key as keyof Pick<FilterValues, 'celebrities'>]?.filter(item_2 => item_2 !== filter.value))} /></div><div className="mt-4 flex flex-row gap-2"><button onClick={() => {
                handleLocalFilterChange("celebrities", filters.celebrities);
                handleLocalFilterChange("celebrityName", filters.celebrityName ?? "");
                handleLocalFilterChange("price", filters.price);
                setIsOpen(false);
              }} className="h-[36px] grow rounded-lg border-2 border-white/10 font-['Montserrat'] text-xs font-bold uppercase text-white">{t("filter.remove")}</button><button onClick={() => {
                handleFilterChange("celebrities", localFilters.celebrities ?? []);
                handleFilterChange("celebrityName", localFilters.celebrityName ?? "");
                handleFilterChange("price", localFilters.price);
                setIsOpen(false);
              }} className="h-[36px] grow rounded-lg bg-white font-['Montserrat'] text-xs font-bold uppercase text-[#392f99]">{t("filter.apply")}</button></div></div></motion.div>}</AnimatePresence></>;
    $[95] = filters.celebrities;
    $[96] = filters.celebrityName;
    $[97] = filters.price;
    $[98] = filters.sortBy;
    $[99] = filters.sortType;
    $[100] = initialFilters.type;
    $[101] = isOpen;
    $[102] = localAppliedFilters;
    $[103] = localFilters;
    $[104] = t;
    $[105] = tags;
    $[106] = t52;
  } else {
    t52 = $[106];
  }
  const renderMobileFilter = t52;
  let t53;
  if ($[107] !== isMobile || $[108] !== renderDesktopFilter || $[109] !== renderMobileFilter) {
    t53 = isMobile ? renderMobileFilter() : renderDesktopFilter();
    $[107] = isMobile;
    $[108] = renderDesktopFilter;
    $[109] = renderMobileFilter;
    $[110] = t53;
  } else {
    t53 = $[110];
  }
  return t53;
};
function _temp(id_0) {
  return id_0.toString();
}